import { z } from 'zod';
import { roleNameEnumSchema } from '../schemas/role.js';
import { userSchema } from '../schemas/user.js';
import { idParamSchema, listQueryBase, multiFilter, paginatedResponse } from './_shared.js';
/**
 * Admin user row (`GET /api/users` items). Adds the per-team role grants
 * the admin tab renders without a second round trip. `roleName` mirrors the
 * legacy admin-hooks shape for call-site stability; `teamId` is null on
 * global grants (super_admin, marketplace_admin). `teamName` is the joined
 * team slug, null on global grants.
 */
export const adminUserRowSchema = userSchema.extend({
    roles: z.array(z.object({
        roleName: z.string().min(1),
        teamId: z.string().uuid().nullable(),
        teamName: z.string().nullable(),
    })),
    /**
     * Unified per-team membership summary (admin via user_roles, reviewer/member
     * via team_associations). One entry per team the user relates to in any
     * capacity. First-class team affiliation for the admin Users tab — see
     * `getUserTeamRoles` for the richer per-team query the detail sheet uses.
     */
    teams: z.array(z.object({
        teamId: z.string().uuid(),
        teamName: z.string(),
        teamSlug: z.string(),
        isAdmin: z.boolean(),
        isReviewer: z.boolean(),
        isMember: z.boolean(),
    })),
    /**
     * Whether this user is present in the ADO org graph. Populated ONLY when
     * the list was requested with `?withAdoOrgMembership=true` AND the org
     * snapshot is warm; `null` otherwise (not requested / dev / cold
     * snapshot). Users enter the org graph via grant-time roster
     * materialization — never by signing into ADO themselves — so this
     * reflects that materialization, not an ADO sign-in history. `AdminPicker`
     * no longer disables non-members on this signal: a `false` row stays
     * selectable and surfaces a non-blocking informational hint instead;
     * absence/null means "unknown".
     */
    adoOrgMember: z.boolean().nullable().optional(),
});
/**
 * Base object schema for list + export. `roleName` is a comma-joined
 * multi-value filter over the canonical role names (validated server-side;
 * each token must be one of the enum values). `isTrusted` filters to trusted
 * (`true`) or untrusted (`false`) users only.
 *
 * `roleName` paired with `teamId` narrows to grants on that specific team
 * (e.g. `roleName=team_admin&teamId=<uuid>`). `teamId` alone (no `roleName`)
 * means "any active grant on this team" — the Team page's members list uses
 * this so it no longer client-filters a capped global page (which dropped
 * members once the user count crossed `pageSize`); each field is validated
 * independently and a bare `teamId` is intentionally allowed.
 */
const listUsersQueryBase = listQueryBase.extend({
    roleName: multiFilter(roleNameEnumSchema.options),
    teamId: z.string().uuid().optional(),
    withAdoOrgMembership: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => v === 'true'),
    isTrusted: z.enum(['true', 'false']).optional(),
});
/**
 * `GET /api/users` query — pagination + sort + free-form `search` (ILIKE).
 * Optional `roleName` multi-filter (comma-joined) filters to users holding
 * any of the named roles; with `teamId`, narrows to grants on that team;
 * `teamId` alone means any active grant on the team. `isTrusted` filters by
 * the trusted flag.
 */
export const listUsersQuerySchema = listUsersQueryBase;
/**
 * `GET /api/users/export` — same filters + sort as the list, without
 * pagination. Used to stream all matching rows as a `text/csv` attachment.
 * `withAdoOrgMembership` is omitted: the export's fixed columns
 * (Name/Email/Roles/Teams/Subject/Trusted/Last sign-in) carry no
 * ADO-membership column, and `exportUsers` ignores it — so accepting it would
 * be a misleading param.
 */
export const exportUsersQuerySchema = listUsersQueryBase.omit({
    page: true,
    pageSize: true,
    withAdoOrgMembership: true,
});
export const listUsersResponseSchema = paginatedResponse(adminUserRowSchema);
/** Re-export of the canonical id-param schema for naming clarity at call sites. */
export const userIdParamSchema = idParamSchema;
/**
 * `GET /api/users/:id` — single-resource fetch. Returns the same row shape
 * as the list (`adminUserRowSchema`) so callers can re-load the canonical
 * row after a PATCH without re-listing+filtering.
 */
export const getUserResponseSchema = adminUserRowSchema;
/**
 * Role-delta payload (§6.1). Snake_case wire fields preserve the existing
 * UI/API shape; the camelCase `RoleAssignmentDelta` alias is the consumer-
 * friendly view.
 */
const roleDeltaSchema = z
    .object({
    role_name: z.string().min(1).max(64),
    team_id: z.string().uuid().nullable(),
})
    .strict();
export const updateUserRolesBodySchema = z
    .object({
    add: z.array(roleDeltaSchema).default([]),
    remove: z.array(roleDeltaSchema).default([]),
})
    .strict()
    .refine((b) => b.add.length + b.remove.length > 0, {
    message: 'at least one of add or remove must be non-empty',
});
/**
 * Response of `PATCH /api/users/:id/roles` — counts of applied deltas. The
 * service returns `{added, removed}` numbers (the actual deltas come from
 * the request body, so re-emitting them is redundant). The UI uses these
 * counts for an "added X / removed Y" toast.
 */
export const updateUserRolesResponseSchema = z.object({
    added: z.number().int().min(0),
    removed: z.number().int().min(0),
});
export const updateUserTrustedBodySchema = z.object({ isTrusted: z.boolean() }).strict();
/**
 * `isTrusted` toggle returns the affected user's id, the new flag, and the
 * post-update timestamp. The slim shape matches the service-layer
 * `UserTrustedRow` so the route doesn't need to re-load the full admin row.
 */
export const updateUserTrustedResponseSchema = z.object({
    id: z.string().uuid(),
    isTrusted: z.boolean(),
    updatedAt: z.string().datetime(),
});
/**
 * One row of `GET /api/users/:id/team-roles` — a team the user has an active
 * admin and/or reviewer relationship with. `teamName` is a directly-renderable
 * label (server `COALESCE(display_name, name)`); `teamSlug` (`teams.name`) is
 * the `/teams/:slug` route param for the cross-link.
 *
 * `adminAdoSyncPending` / `reviewerAdoSyncPending` are the roster row's
 * `ado_sync_pending`, or `false` when that roster is absent. They are ONLY
 * meaningful when the matching `isAdmin` / `isReviewer` is true: a `false`
 * value conflates three states — "not a member of that roster", "member and
 * ADO-synced", and "roster absent". Always gate on `is*` before reading the
 * paired `*AdoSyncPending`.
 *
 * `isMember` is the plain (non-RBAC, non-ADO) team-member tier. It carries NO
 * `*AdoSyncPending` companion — members are never mirrored to ADO.
 */
export const userTeamRoleSchema = z.object({
    teamId: z.string().uuid(),
    teamName: z.string(),
    teamSlug: z.string(),
    isAdmin: z.boolean(),
    isReviewer: z.boolean(),
    isMember: z.boolean(),
    adminAdoSyncPending: z.boolean(),
    reviewerAdoSyncPending: z.boolean(),
});
/**
 * `GET /api/users/:id/team-roles` — the user's team admin/reviewer memberships
 * for the Teams tab of the admin user sheet.
 *
 * `adoOrgMembershipStatus` is the authoritative ADO-org membership probe state:
 * - `'in_org'` — the user is in the ADO org graph (grants will sync).
 * - `'not_in_org'` — confirmed absent from the ADO org graph. Grants are
 *   NOT blocked on this: the org-probe grant gate was retired in favor of
 *   identity-resolution-time gating (`USER_NOT_IN_DIRECTORY` /
 *   `USER_IDENTITY_AMBIGUOUS` / `USER_IDENTITY_CONFLICT` on the resolve/
 *   override endpoints). The panel shows a non-blocking hint, nothing more.
 * - `'unknown'` — a probe was attempted but failed/degraded (e.g. ADO/Redis
 *   unreachable). There is no grant-side probe left to "fail open" from —
 *   this is display-only degradation: the panel surfaces a "couldn't
 *   verify" note so the operator knows the membership figure may be stale,
 *   not a guard that was skipped.
 * - `'no_probe'` — no probe wired (local dev); silent, no warning.
 * - `'unresolved'` — the user's ADO oid has not been resolved yet (Graph
 *   lookup pending / not_found / ambiguous). Grants will attempt resolution
 *   on the fly.
 *
 * `adoOrgMember` is the derived convenience boolean kept for existing callers:
 * `in_org→true`, `not_in_org→false`, `unknown`/`no_probe→null`. Prefer
 * `adoOrgMembershipStatus` when the degraded-vs-dev distinction matters.
 */
export const getUserTeamRolesResponseSchema = z.object({
    adoOrgMember: z.boolean().nullable(),
    adoOrgMembershipStatus: z.enum(['in_org', 'not_in_org', 'unknown', 'no_probe', 'unresolved']),
    teams: z.array(userTeamRoleSchema),
});
/**
 * A single Microsoft Graph match returned when resolution is `ambiguous`.
 * `id` is the Graph object id (Entra oid) — always a GUID.
 */
const adoIdentityCandidateSchema = z
    .object({
    id: z.string().uuid(),
    displayName: z.string().nullable(),
    mail: z.string().nullable(),
    userPrincipalName: z.string().nullable(),
})
    .strict();
/**
 * Response of `POST /api/users/:id/ado-identity/resolve` — re-runs
 * `ensureOriginId` for the user. Discriminated on `status`, mirroring the
 * resolver's outcome; the invariants that used to be documented ("originId
 * only on resolved", "candidates only on ambiguous") are now structural —
 * each variant's shape is the only shape zod will accept for that status:
 * - `'resolved'` — `originId` is set (freshly resolved or already linked).
 * - `'not_found'` — no matching Graph user for the user's email.
 * - `'ambiguous'` — multiple Graph matches; `candidates` lists them for the
 *   admin to pick from via the override endpoint. An empty array IS valid
 *   here — the candidate-detail fetch is a best-effort follow-up to the
 *   count check and can come back empty on a degraded Graph call. When
 *   populated, treat the count as a floor, not an exact total: the Graph
 *   query behind the ambiguity check uses `$top=2`, so "ambiguous" only
 *   ever means "two or more matches", never a precise count.
 * - `'conflict'` — the resolved oid is already linked to a different user.
 * - `'transient'` — the Graph lookup failed (network/5xx); retryable.
 */
export const resolveAdoIdentityResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('resolved'), originId: z.string().uuid() }).strict(),
    z
        .object({ status: z.literal('ambiguous'), candidates: z.array(adoIdentityCandidateSchema) })
        .strict(),
    z.object({ status: z.literal('not_found') }).strict(),
    z.object({ status: z.literal('conflict') }).strict(),
    z.object({ status: z.literal('transient') }).strict(),
]);
/**
 * Body of `PUT /api/users/:id/ado-identity` — manual override of a user's
 * ADO origin id (Graph object id). Used when resolution is ambiguous or
 * not_found and an admin picks/enters the correct oid directly. `null` is
 * an explicit, audited clear of the mapping — the admin-recovery path for
 * unlinking a wrongly-set origin id without waiting for a fresh resolve.
 */
export const overrideAdoIdentityBodySchema = z
    .object({ originId: z.string().uuid().nullable() })
    .strict();
/**
 * Response of `PUT /api/users/:id/ado-identity` — echoes the persisted
 * origin id; `null` after a clear.
 */
export const overrideAdoIdentityResponseSchema = z
    .object({
    originId: z.string().uuid().nullable(),
})
    .strict();
//# sourceMappingURL=users.js.map